import React, { useEffect, useRef, useState } from 'react';
import ExcelJS from 'exceljs';
import {
  looksLikePhoneNumber,
  toValidZimbabwePhone,
  ZIMBABWE_PHONE_HINT,
} from '../../utils/phoneNumber';
import { excelCellToString } from '../../utils/excelCell';

interface ImportContactsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (contacts: string[]) => void;
}

function collectValidNumbers(values: Array<string | number>): {
  numbers: string[];
  skipped: number;
} {
  const numbers: string[] = [];
  let skipped = 0;

  values.forEach((value) => {
    const valid = toValidZimbabwePhone(value);
    if (valid) {
      if (!numbers.includes(valid)) {
        numbers.push(valid);
      }
      return;
    }
    if (looksLikePhoneNumber(value)) {
      skipped += 1;
    }
  });

  return { numbers, skipped };
}

export const ImportContactsModal: React.FC<ImportContactsModalProps> = ({
  isOpen,
  onClose,
  onSave,
}) => {
  const [uploadedNumbers, setUploadedNumbers] = useState<string[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) {
      setUploadedNumbers([]);
      setIsDragging(false);
      setError(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const applyParsedNumbers = (numbers: string[], skipped: number) => {
    if (numbers.length === 0) {
      setUploadedNumbers([]);
      setError(
        `No valid phone numbers found in the file. ${ZIMBABWE_PHONE_HINT}`
      );
      return;
    }

    setUploadedNumbers(numbers);
    setError(
      skipped > 0
        ? `${skipped} invalid phone number(s) were skipped. ${ZIMBABWE_PHONE_HINT}`
        : null
    );
  };

  const processCSVFile = (file: File) => {
    setError(null);
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const content = (e.target?.result as string) || '';
        const values = content
          .split(/\r?\n/)
          .flatMap((line) => line.split(/[,;\t]/))
          .map((n) => n.trim())
          .filter(Boolean);
        const { numbers, skipped } = collectValidNumbers(values);
        applyParsedNumbers(numbers, skipped);
      } catch {
        setError('Error processing file. Please ensure it\'s a valid CSV file.');
      }
    };

    reader.onerror = () => {
      setError('Error reading file. Please try again.');
    };

    reader.readAsText(file);
  };

  const processExcelFile = (file: File) => {
    setError(null);
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const data = e.target?.result as ArrayBuffer;
        if (!data) {
          setError('Error reading file. Please try again.');
          return;
        }

        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(data);
        const values: Array<string | number> = [];

        workbook.worksheets.forEach((worksheet) => {
          worksheet.eachRow((row) => {
            row.eachCell({ includeEmpty: false }, (cell) => {
              const cellValue = excelCellToString(cell.value);
              if (cellValue) values.push(cellValue);
            });
          });
        });

        const { numbers, skipped } = collectValidNumbers(values);
        applyParsedNumbers(numbers, skipped);
      } catch (err) {
        console.error('Excel processing error:', err);
        setError(
          'Error processing Excel file. Please ensure the file contains phone numbers in text format.'
        );
      }
    };

    reader.onerror = () => {
      setError('Error reading file. Please try again.');
    };

    reader.readAsArrayBuffer(file);
  };

  const processFile = (file: File) => {
    const lower = file.name.toLowerCase();
    if (lower.endsWith('.csv') || lower.endsWith('.txt')) {
      processCSVFile(file);
    } else if (lower.endsWith('.xlsx') || lower.endsWith('.xls')) {
      processExcelFile(file);
    } else {
      setError('Please upload a valid CSV or Excel file.');
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      processFile(file);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      processFile(file);
    }
    event.target.value = '';
  };

  const handleSave = () => {
    if (uploadedNumbers.length === 0) return;
    onSave(uploadedNumbers);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Import Contacts</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center ${
            isDragging ? 'border-[#FF5722] bg-orange-50' : 'border-gray-300'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept=".csv,.txt,.xlsx,.xls"
            className="hidden"
          />
          <div className="space-y-4">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
            <div className="text-sm text-gray-600">
              <button
                onClick={() => fileInputRef.current?.click()}
                className="text-[#FF5722] hover:text-[#F4511E] font-medium"
              >
                Click to upload
              </button>
              {' or drag and drop'}
            </div>
            <p className="text-xs text-gray-500">
              CSV or Excel file with phone numbers (263XXXXXXXXX)
            </p>
          </div>
        </div>

        {error && (
          <div className="mt-4 p-3 bg-red-50 text-red-600 rounded-md text-sm">
            {error}
          </div>
        )}

        {uploadedNumbers.length > 0 && (
          <div className="mt-6">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-sm font-medium text-gray-700">
                Imported Contacts ({uploadedNumbers.length})
              </h3>
            </div>
            <div className="border rounded-md p-4">
              <div className="space-y-2">
                {uploadedNumbers.slice(0, 5).map((number) => (
                  <div key={number} className="bg-gray-50 p-2 rounded">
                    {number}
                  </div>
                ))}
                {uploadedNumbers.length > 5 && (
                  <div className="text-center py-2 text-xs text-gray-500 italic">
                    ... and {uploadedNumbers.length - 5} more contact(s)
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="mt-6 flex justify-end space-x-4">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={uploadedNumbers.length === 0}
            className={`px-4 py-2 rounded-md transition-colors ${
              uploadedNumbers.length > 0
                ? 'bg-[#FF5722] text-white hover:bg-[#F4511E]'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            Import Contacts
          </button>
        </div>
      </div>
    </div>
  );
};
